import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFeaturedCoupons, useCategories, useRecommendedCoupons, usePublicBanners } from '../../hooks/useCoupons';
import api from '../../services/api';
import { useWallet } from '../../hooks/useWallet';
import { useAuth } from '../../context/AuthContext';
import CouponCard from '../../components/coupon/CouponCard';
import RedemptionModal from '../../components/coupon/RedemptionModal';
import { LoadingPage } from '../../components/common/UI';

const CATEGORY_ICONS = {
  travel: '✈️', retail: '🛍️', dining: '🍽️', 'health-wellness': '💪',
  entertainment: '🎬', 'financial-services': '💳', auto: '🚗', technology: '💻',
  fashion: '👗', beauty: '💄', home: '🏡', grocery: '🛒', default: '🏷️',
};

const TOP_BRANDS = ['amazon', 'adidas', 'zomato', 'Myntra', 'PUMA', 'Nike', 'Target', 'Walmart'];

const REC_BADGE_LABELS = {
  similar_shoppers:       '👥 Shoppers like you',
  matches_your_interests: '👍 For you',
  favorite_brand:         '⭐ A brand you love',
  trending:               '🔥 Trending',
  recently_verified:      '✓ Just verified',
  editor_pick:            '⭐ Editor pick',
  popular:                'Popular',
};

function recBadge(reasons) {
  if (!reasons || !reasons.length) return null;
  // Show the most informative reason. Priority order: collab signal first
  // (strongest indicator of fit), then content affinity, then trending/fresh,
  // then a generic "Popular" fallback for the anonymous case.
  for (const r of ['similar_shoppers','matches_your_interests','favorite_brand','trending','editor_pick','recently_verified','popular']) {
    if (reasons.includes(r)) return REC_BADGE_LABELS[r];
  }
  return null;
}

export default function HomePage() {
  const { user } = useAuth();
  const { coupons: featured, loading } = useFeaturedCoupons(8);
  const { coupons: recommended, personalized, loading: recLoading } = useRecommendedCoupons(8);
  const { categories } = useCategories();
  const homepageBanners = usePublicBanners('homepage_top');
  const [redeemCoupon, setRedeemCoupon] = useState(null);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/coupons?search=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true" style={{ backgroundImage: `url(/images/hero-bg.jpg)` }} />
        <div className="container">
          <div className="hero-inner">
            <div className="hero-content">
              {user ? (
                <SignedInHero user={user} search={search} setSearch={setSearch} handleSearch={handleSearch} />
              ) : (
                <SignedOutHero search={search} setSearch={setSearch} handleSearch={handleSearch} />
              )}
            </div>

            <div className="hero-visual-float" aria-hidden="true">
              {/* Reserved — the hero-bg handles visuals on desktop; keep empty column */}
            </div>
          </div>
        </div>

        {/* Brand trust strip */}
        <div style={{ position: 'relative', zIndex: 2, borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(14,15,46,0.55)', backdropFilter: 'blur(10px)' }}>
          <div className="container pv-brandstrip" style={{ padding: '18px 20px' }}>
            <p className="pv-brandstrip-label" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 500 }}>
              Loved by thousands.<br style={{ display: 'block' }} />
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>Partnered with leading brands.</span>
            </p>
            <div className="pv-brandstrip-list">
              {TOP_BRANDS.map(b => (
                <span key={b} style={{ color: 'rgba(255,255,255,0.55)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>{b}</span>
              ))}
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, fontStyle: 'italic' }}>& more</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ADMIN-MANAGED BANNERS (homepage_top) ============ */}
      {homepageBanners.length > 0 && (
        <section style={{ background: '#fff', padding: '32px 0 0' }}>
          <div className="container" style={{ display: 'grid', gap: 16, gridTemplateColumns: homepageBanners.length > 1 ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr' }}>
            {homepageBanners.map(b => <HomeBanner key={b.id} banner={b} />)}
          </div>
        </section>
      )}

      {/* ============ STATS ============ */}
      <section className="section-sm" style={{ background: '#fff' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }} className="stats-grid">
            <StatBlock n="10,000+" label="Verified Deals" icon="🔥" />
            <StatBlock n="500+"    label="Trusted Brands" icon="🏪" />
            <StatBlock n="$2.4M+"  label="Saved by Members" icon="💰" />
            <StatBlock n="4.8★"    label="Customer Rating" icon="⭐" />
          </div>
          <style>{`@media(max-width:720px){.stats-grid{grid-template-columns:1fr 1fr!important}}`}</style>
        </div>
      </section>

      {/* ============ CATEGORIES ============ */}
      {categories.length > 0 && (
        <section className="section section-tinted">
          <div className="container">
            <div className="text-center" style={{ marginBottom: 48 }}>
              <span className="eyebrow">Shop by Category</span>
              <h2 className="section-h2" style={{ marginTop: 12 }}>Deals for every side of you</h2>
              <p className="section-lead">From weekend takeout to weekend escapes, find curated perks across the categories you care about.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 }}>
              {categories.slice(0, 12).map(cat => (
                <Link key={cat.id} to={`/coupons?category=${cat.slug}`} className="cat-tile">
                  <div className="cat-icon">{CATEGORY_ICONS[cat.slug] || '🏷️'}</div>
                  <p className="cat-name">{cat.name}</p>
                  {cat.coupons_count > 0 && <p className="cat-count">{cat.coupons_count} deals</p>}
                </Link>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 36 }}>
              <Link to="/coupons" className="btn btn-outline">Browse all categories →</Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ FEATURED DEALS ============ */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div className="section-header">
            <div>
              <span className="eyebrow">⭐ Hand-picked for you</span>
              <h2 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 8 }}>Featured Deals</h2>
            </div>
            <Link to="/coupons?featured=true" className="btn btn-outline">See all deals →</Link>
          </div>
          {loading ? <LoadingPage /> : (
            featured.length > 0 ? (
              <div className="grid-coupons">
                {featured.map(c => <CouponCard key={c.id} coupon={c} onRedeem={setRedeemCoupon} />)}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#8B94A8', background: '#F7F8FC', borderRadius: 16 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
                <p style={{ fontSize: 16, fontWeight: 500 }}>Fresh deals dropping soon.</p>
                <p style={{ fontSize: 13, marginTop: 4 }}>Check back — we're curating the next batch.</p>
              </div>
            )
          )}
        </div>
      </section>

      {/* ============ RECOMMENDED FOR YOU ============ */}
      {!recLoading && recommended.length > 0 && (
        <section className="section" style={{ background: 'linear-gradient(180deg, #fff 0%, #F7F8FC 100%)' }}>
          <div className="container">
            <div className="section-header">
              <div>
                <span className="eyebrow">{personalized && user ? '✨ Personalized for you' : '🔥 Trending right now'}</span>
                <h2 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', marginTop: 8 }}>
                  {personalized && user ? `Recommended, ${user.name?.split(' ')[0]}` : 'Trending deals'}
                </h2>
                <p style={{ color: '#8B94A8', fontSize: 14, marginTop: 4 }}>
                  {personalized
                    ? "Based on the categories you've been clicking and redeeming."
                    : "Most-clicked offers in the last 30 days. Sign in for personalized picks."}
                </p>
              </div>
              <Link to="/coupons" className="btn btn-outline">All deals →</Link>
            </div>
            <div className="grid-coupons">
              {recommended.map(c => (
                <CouponCard
                  key={`rec-${c.id}`}
                  coupon={c}
                  onRedeem={setRedeemCoupon}
                  badge={recBadge(c.recommendation_reasons)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ WHY PERKVORA ============ */}
      <section className="section section-tinted">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 56 }}>
            <span className="eyebrow">Why PerkVora</span>
            <h2 className="section-h2" style={{ marginTop: 12 }}>Built for the way you actually shop</h2>
            <p className="section-lead">No clipping, no printing, no playing the code-that-actually-works lottery. Just verified savings and real cashback.</p>
          </div>
          <div className="grid-3">
            <Feature icon="✓" title="Verified, not wishful" body="Every code is tested before it goes live. 'Last verified 2 hours ago' on every deal — if it stops working, we take it down." />
            <Feature icon="⚡" title="One-click savings" body="No forms, no membership walls, no fake 'newsletter sign-up required'. See the deal, click, save. That's it." />
            <Feature icon="💰" title="Real cashback, real money" body="Earn cashback on every redemption. Transfer it straight to your bank. Your money, your rules." />
            <Feature icon="🔒" title="Private by design" body="We never sell your data. Period. CCPA + GDPR compliant, and we won't ask for anything we don't need." />
            <Feature icon="🎯" title="Smarter with every visit" body="Your feed gets better the more you use it. Personalized recommendations that actually match what you buy." />
            <Feature icon="🌎" title="Brands you love" body="From your local favorite to national chains — 500+ merchants across travel, dining, retail, wellness, and more." />
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: 72 }}>
            <span className="eyebrow">How it works</span>
            <h2 className="section-h2" style={{ marginTop: 12 }}>Three steps to your next save</h2>
            <p className="section-lead">Start saving in under 60 seconds. Free forever, no credit card required.</p>
          </div>
          <div className="grid-3" style={{ gap: 32 }}>
            <Step n="1" title="Find a perk" desc="Browse 10,000+ verified deals across your favorite brands. Filter by category, location, or discount type." />
            <Step n="2" title="Claim it in one click" desc="Grab the code, activate cashback, or unlock the exclusive link — no printing, no sign-ups required." />
            <Step n="3" title="Save & earn" desc="Apply at checkout and save instantly. Earn PerkVora points and cashback on every qualifying purchase." />
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/how-it-works" className="btn btn-primary btn-lg">Learn more →</Link>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="section section-tinted">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 48 }}>
            <span className="eyebrow">What members say</span>
            <h2 className="section-h2" style={{ marginTop: 12 }}>Real people. Real savings.</h2>
            <p className="section-lead">Thousands of smart shoppers use PerkVora every week. Here's what keeps them coming back.</p>
          </div>
          <div className="grid-3">
            <Testimonial
              quote="I've tried every coupon site out there. PerkVora is the first one where the codes actually work. Saved $340 in my first month."
              name="Jessica M." meta="Austin, TX · Member since 2025" initial="J" />
            <Testimonial
              quote="The cashback adds up fast. I redeemed $80 last month just from grocery and travel deals. It's my new default before any purchase."
              name="Marcus R." meta="Brooklyn, NY · Pro Member" initial="M" />
            <Testimonial
              quote="Clean, fast, no pop-ups. Actually respects my time. The brand deals are legit exclusive — not the same recycled codes you see everywhere."
              name="Priya K." meta="San Francisco, CA · Gold Tier" initial="P" />
          </div>
        </div>
      </section>

      {/* ============ CASHBACK TEASER ============ */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container-wide">
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56, alignItems: 'center' }} className="cashback-grid">
            <div>
              <span className="eyebrow">Cashback wallet</span>
              <h2 style={{ fontSize: 'clamp(28px, 3.4vw, 40px)', fontWeight: 800, letterSpacing: '-0.025em', marginTop: 12, marginBottom: 18, lineHeight: 1.15 }}>
                Earn <span className="gradient-text">real money</span> on every purchase
              </h2>
              <p style={{ fontSize: 16, color: '#4A5568', lineHeight: 1.7, marginBottom: 28 }}>
                Stack cashback on top of coupons. Redeem as cash to your bank via ACH — starting from just $5. No points to decode, no expiration tricks.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
                <CashbackRow icon="💸" title="Up to 25% back" desc="On 500+ partner brands" />
                <CashbackRow icon="⚡" title="Fast payouts" desc="Straight to your bank via ACH in days" />
                <CashbackRow icon="🎯" title="Stack with coupons" desc="Discount + cashback on the same order" />
              </div>
              <Link to="/how-it-works#cashback" className="btn btn-primary btn-lg">
                How cashback works →
              </Link>
            </div>
            <div style={{ background: 'var(--brand-gradient)', borderRadius: 24, padding: 40, color: '#fff', position: 'relative', overflow: 'hidden', minHeight: 380 }}>
              <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
              <div style={{ position: 'absolute', bottom: -60, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ position: 'relative' }}>
                <p style={{ fontSize: 13, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>PerkVora Wallet</p>
                <p style={{ fontSize: 56, fontWeight: 800, marginTop: 8, letterSpacing: '-0.03em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>$127.50</p>
                <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 32 }}>Available cashback</p>
                <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 12, padding: 16, backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.15)', marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600 }}>Nike</p>
                      <p style={{ fontSize: 11, opacity: 0.7 }}>2 days ago</p>
                    </div>
                    <p style={{ fontSize: 15, fontWeight: 700 }}>+$12.40</p>
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 12, padding: 16, backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600 }}>Expedia</p>
                      <p style={{ fontSize: 11, opacity: 0.7 }}>1 week ago</p>
                    </div>
                    <p style={{ fontSize: 15, fontWeight: 700 }}>+$48.20</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <style>{`@media(max-width:880px){.cashback-grid{grid-template-columns:1fr!important;gap:32px!important}}`}</style>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="section">
        <div className="container">
          <div className="cta-banner">
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.9, marginBottom: 14 }}>Free forever · No credit card</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 16, lineHeight: 1.1 }}>
              Start saving in under 60 seconds.
            </h2>
            <p style={{ fontSize: 17, maxWidth: 560, margin: '0 auto 32px', opacity: 0.95, lineHeight: 1.55 }}>
              Join thousands of smart shoppers getting exclusive perks, real cashback, and first access to the best deals online.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" className="btn btn-xl" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700 }}>
                Create free account →
              </Link>
              <Link to="/coupons" className="btn btn-outline-light btn-xl">
                Browse deals first
              </Link>
            </div>
          </div>
        </div>
      </section>

      {redeemCoupon && <RedemptionModal coupon={redeemCoupon} onClose={() => setRedeemCoupon(null)} />}
    </div>
  );
}

/* ======= sub-components ======= */

/**
 * Marketing hero — anonymous visitors. Conversion-oriented copy,
 * full CTA stack to push them toward signup or browsing deals.
 */
function SignedOutHero({ search, setSearch, handleSearch }) {
  return (
    <>
      <div className="hero-ticker">
        <span className="hero-ticker-dot" />
        <span>🎉 New deals added every day</span>
      </div>

      <img src="/images/logo-white.svg" alt="PerkVora" className="hero-logo"
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
      />
      <div style={{ display: 'none', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', fontWeight: 800, fontSize: 48, letterSpacing: '-0.03em', color: '#fff', marginBottom: 20, lineHeight: 1 }}>
        Perk<span className="gradient-text">Vora</span>
        <div style={{ fontSize: 11, letterSpacing: '0.35em', fontWeight: 500, marginTop: 6, opacity: 0.7, textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Perks that value you</div>
      </div>

      <h1 className="hero-headline">
        Amazing perks.<br />
        Real savings. <span className="gradient-text">Every day.</span>
      </h1>

      <p className="hero-sub">
        Your all-in-one platform for exclusive deals, top brands,
        and life's little wins.
      </p>

      <div className="hero-chips">
        <HeroFeature icon="🏷️" label="Exclusive Discounts" />
        <HeroFeature icon="🎁" label="Top Brands You Love" />
        <HeroFeature icon="%" label="New Deals Every Day" />
        <HeroFeature icon="🔒" label="100% Secure & Trusted" />
      </div>

      <form onSubmit={handleSearch} style={{ display: 'flex', maxWidth: 560, marginBottom: 24, background: '#fff', borderRadius: 999, padding: 6, boxShadow: '0 12px 32px rgba(14,15,46,0.25)' }}>
        <span style={{ display: 'flex', alignItems: 'center', padding: '0 6px 0 18px', color: '#8B94A8', fontSize: 18 }}>🔍</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search for brands, categories, or deals…"
          style={{ flex: 1, padding: '14px 10px', border: 'none', fontSize: 15, outline: 'none', color: '#0F1225', background: 'transparent', minWidth: 0 }}
        />
        <button type="submit" className="btn btn-primary" style={{ borderRadius: 999, padding: '12px 24px', fontSize: 14 }}>Search</button>
      </form>

      <div className="hero-cta-row">
        <Link to="/coupons" className="btn btn-primary btn-xl">
          Explore Deals Now <span style={{ marginLeft: 4 }}>→</span>
        </Link>
        <Link to="/how-it-works" className="btn btn-outline-light btn-xl">▶ See How It Works</Link>
      </div>
    </>
  );
}

/**
 * Logged-in hero — replaces the marketing pitch with a personalized
 * "Welcome back" banner, a live cashback balance card (lazy-loaded
 * via useWallet so it doesn't block anonymous renders), and CTAs
 * pointing at the deal feed + their wallet. The search bar stays
 * because it's useful in either state.
 */
function SignedInHero({ user, search, setSearch, handleSearch }) {
  const { wallet, loading: walletLoading } = useWallet();
  const firstName = (user?.name || '').split(' ')[0] || 'there';
  const available = Number(wallet?.available ?? 0);
  const lifetime  = Number(wallet?.lifetime ?? 0);
  const pending   = Number(wallet?.pending ?? 0);

  return (
    <>
      <div className="hero-ticker">
        <span className="hero-ticker-dot" />
        <span>👋 Welcome back, {firstName}</span>
      </div>

      <h1 className="hero-headline" style={{ marginBottom: 12 }}>
        Welcome back,<br />
        <span className="gradient-text">{firstName}.</span>
      </h1>

      <p className="hero-sub" style={{ marginBottom: 28 }}>
        Pick up where you left off — fresh deals, your saved favorites, and your cashback wallet are all one click away.
      </p>

      {/* Wallet glance card */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, maxWidth: 560,
        marginBottom: 24, padding: 16, borderRadius: 14,
        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
        backdropFilter: 'blur(8px)',
      }}>
        <BalanceTile label="Available" value={available} accent loading={walletLoading} />
        <BalanceTile label="Pending"   value={pending}   loading={walletLoading} />
        <BalanceTile label="Lifetime"  value={lifetime}  loading={walletLoading} />
      </div>

      <form onSubmit={handleSearch} style={{ display: 'flex', maxWidth: 560, marginBottom: 24, background: '#fff', borderRadius: 999, padding: 6, boxShadow: '0 12px 32px rgba(14,15,46,0.25)' }}>
        <span style={{ display: 'flex', alignItems: 'center', padding: '0 6px 0 18px', color: '#8B94A8', fontSize: 18 }}>🔍</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search for brands, categories, or deals…"
          style={{ flex: 1, padding: '14px 10px', border: 'none', fontSize: 15, outline: 'none', color: '#0F1225', background: 'transparent', minWidth: 0 }}
        />
        <button type="submit" className="btn btn-primary" style={{ borderRadius: 999, padding: '12px 24px', fontSize: 14 }}>Search</button>
      </form>

      <div className="hero-cta-row">
        <Link to="/coupons" className="btn btn-primary btn-xl">
          Browse Deals <span style={{ marginLeft: 4 }}>→</span>
        </Link>
        <Link to="/profile/wallet" className="btn btn-outline-light btn-xl">💰 Your Wallet</Link>
      </div>
    </>
  );
}

function BalanceTile({ label, value, accent, loading }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </p>
      <p style={{
        // Scales down on narrow phones so a four-figure balance still fits
        // a third of a 320px screen.
        fontSize: 'clamp(15px, 5vw, 20px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1,
        fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', overflowWrap: 'anywhere',
        color: accent ? '#fff' : 'rgba(255,255,255,0.85)',
        ...(accent ? { background: 'var(--brand-gradient-text)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' } : {}),
      }}>
        {loading ? '—' : `$${value.toFixed(2)}`}
      </p>
    </div>
  );
}

function HeroFeature({ icon, label }) {
  return (
    <span className="chip">
      <span style={{ width: 20, height: 20, borderRadius: 6, background: 'var(--brand-gradient)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff' }}>{icon}</span>
      {label}
    </span>
  );
}

function StatBlock({ n, label, icon }) {
  return (
    <div style={{ textAlign: 'center', padding: 20 }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <p style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }} className="gradient-text">{n}</p>
      <p style={{ fontSize: 13, color: '#8B94A8', fontWeight: 500, marginTop: 4 }}>{label}</p>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="feature-tile">
      <div className="feature-icon">{icon}</div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-desc">{body}</p>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="step-card">
      <div className="step-num">{n}</div>
      <h3 className="step-title">{title}</h3>
      <p className="step-desc">{desc}</p>
    </div>
  );
}

function Testimonial({ quote, name, meta, initial }) {
  return (
    <div className="testimonial-card">
      <div className="testimonial-stars">★ ★ ★ ★ ★</div>
      <p className="testimonial-quote">"{quote}"</p>
      <div className="testimonial-author">
        <div className="testimonial-avatar">{initial}</div>
        <div>
          <p className="testimonial-name">{name}</p>
          <p className="testimonial-meta">{meta}</p>
        </div>
      </div>
    </div>
  );
}

function HomeBanner({ banner }) {
  // Click counter is best-effort — never block navigation if it fails.
  // cta_url stored in DB can be absolute; relativize on the same origin
  // so banners authored against production don't yank dev visitors away.
  const onClick = (e) => {
    if (!banner.cta_url) { e.preventDefault(); return; }
    api.post(`/banners/${banner.id}/click`).catch(() => {});
  };
  const href = banner.cta_url
    ? (typeof window !== 'undefined' && banner.cta_url.startsWith(window.location.origin)
        ? banner.cta_url.slice(window.location.origin.length) || '/'
        : banner.cta_url.replace(/^https?:\/\/(www\.)?perkvora\.com/i, '') || banner.cta_url)
    : '#';

  const inner = (
    <>
      {banner.image_url && (
        <div style={{
          width: '100%', aspectRatio: '4 / 1',
          backgroundImage: `url(${banner.image_url})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
      )}
      {(banner.title || banner.subtitle || banner.cta_text) && (
        <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', color: '#fff' }}>
          <div>
            {banner.title    && <p style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{banner.title}</p>}
            {banner.subtitle && <p style={{ fontSize: 14, opacity: 0.92, marginTop: 4 }}>{banner.subtitle}</p>}
          </div>
          {banner.cta_text && (
            <span className="btn btn-primary btn-sm" style={{ background: '#fff', color: '#1A1B4B', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {banner.cta_text} →
            </span>
          )}
        </div>
      )}
    </>
  );

  return (
    <a
      href={href}
      onClick={onClick}
      style={{
        display: 'block', textDecoration: 'none', borderRadius: 16, overflow: 'hidden',
        background: 'var(--brand-gradient, linear-gradient(135deg, #6B2FD9 0%, #A059FF 100%))',
        boxShadow: '0 8px 22px rgba(15,18,37,0.08)',
        border: '1px solid #E4E7F0',
      }}
    >
      {inner}
    </a>
  );
}

function CashbackRow({ icon, title, desc }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div>
        <p style={{ fontWeight: 600, fontSize: 15 }}>{title}</p>
        <p style={{ fontSize: 13, color: '#8B94A8' }}>{desc}</p>
      </div>
    </div>
  );
}
